<template>
  <div v-if="contentModules.length">
    <div v-for="(module, index) in contentModules" :key="index" class="content-module-wrapper">
      <IntersectWrapper
        v-if="module.component === ContentModules.COVER"
        @mounted="trackModuleExists(contentModules, module)"
        @intersects="trackModuleViewed(contentModules, module)"
      >
        <CRCover
          v-append-parameters="urlParams"
          :module="module"
          :lazy="index > 2"
          :job-count-string="jobsCountAll"
          @emitted-click="trackModuleClick(contentModules, module)"
        />
      </IntersectWrapper>

      <IntersectWrapper
        v-else-if="module.component === ContentModules.CATEGORIES"
        @mounted="trackModuleExists(contentModules, module)"
        @intersects="trackModuleViewed(contentModules, module)"
      >
        <CRCategories
          v-append-parameters="urlParams"
          :module="module"
          :lazy="index > 2"
          :job-counts="jobsCountCategories"
          :zero-jobs-text="zeroJobsText"
          @emitted-click="
            (e) => {
              trackModuleClick(contentModules, module, e.child);
            }
          "
        />
      </IntersectWrapper>

      <IntersectWrapper
        v-else-if="module.component === ContentModules.CARDS"
        @mounted="trackModuleExists(contentModules, module)"
        @intersects="trackModuleViewed(contentModules, module)"
      >
        <CRCards
          v-append-parameters="urlParams"
          :module="module"
          :lazy="index > 2"
          @emitted-click="
            (e) => {
              trackModuleClick(contentModules, module, e.child);
            }
          "
        />
      </IntersectWrapper>

      <IntersectWrapper
        v-else-if="module.component === ContentModules.BENEFITS"
        @mounted="trackModuleExists(contentModules, module)"
        @intersects="trackModuleViewed(contentModules, module)"
      >
        <CRBenefits
          v-append-parameters="urlParams"
          :module="module"
          :lazy="index > 2"
          @emitted-click="trackModuleClick(contentModules, module)"
        />
      </IntersectWrapper>

      <IntersectWrapper
        v-else-if="module.component === ContentModules.IMAGETEXT"
        @mounted="trackModuleExists(contentModules, module)"
        @intersects="trackModuleViewed(contentModules, module)"
      >
        <CRImageText
          v-append-parameters="urlParams"
          :module="module"
          :lazy="index > 2"
          @emitted-click="trackModuleClick(contentModules, module)"
        />
      </IntersectWrapper>
      <IntersectWrapper
        v-else-if="module.component === ContentModules.TEXT"
        @mounted="trackModuleExists(contentModules, module)"
        @intersects="trackModuleViewed(contentModules, module)"
      >
        <CRText
          v-append-parameters="urlParams"
          :module="module"
          :lazy="index > 2"
          @emitted-click="trackModuleClick(contentModules, module)"
        />
      </IntersectWrapper>

      <IntersectWrapper
        v-else-if="module.component === ContentModules.CHAPTER"
        @mounted="trackModuleExists(contentModules, module)"
        @intersects="trackModuleViewed(contentModules, module)"
      >
        <CRChapter
          v-append-parameters="urlParams"
          :module="module"
          :lazy="index > 2"
          @emitted-click="trackModuleClick(contentModules, module)"
        />
      </IntersectWrapper>

      <IntersectWrapper
        v-else-if="module.component === ContentModules.VIDEO"
        @mounted="trackModuleExists(contentModules, module)"
        @intersects="trackModuleViewed(contentModules, module)"
      >
        <CRVideo
          v-append-parameters="urlParams"
          :module="module"
          :lazy="index > 2"
          @emitted-click="trackModuleClick(contentModules, module)"
        />
      </IntersectWrapper>

      <IntersectWrapper
        v-if="module.component === ContentModules.GALLERY"
        @mounted="trackModuleExists(contentModules, module)"
        @intersects="trackModuleViewed(contentModules, module)"
      >
        <CRGallery
          v-append-parameters="urlParams"
          :module="module"
          :lazy="index > 2"
          @emitted-click="
            (e) => {
              trackModuleClick(contentModules, module, e.child);
            }
          "
        />
      </IntersectWrapper>

      <IntersectWrapper
        v-else-if="module.component === ContentModules.FAQ"
        @mounted="trackModuleExists(contentModules, module)"
        @intersects="trackModuleViewed(contentModules, module)"
      >
        <CRFaqs
          v-append-parameters="urlParams"
          :module="module"
          :lazy="index > 2"
        />
      </IntersectWrapper>

      <IntersectWrapper
        v-else-if="module.component === ContentModules.HIRINGPROCESS"
        @mounted="trackModuleExists(contentModules, module)"
        @intersects="trackModuleViewed(contentModules, module)"
      >
        <CRHiringProcess
          v-append-parameters="urlParams"
          :module="module"
          :lazy="index > 2"
          :labels="hiringProcessLabels"
        />
      </IntersectWrapper>

      <IntersectWrapper
        v-else-if="module.component === ContentModules.SEARCH_AND_FILTER"
        @mounted="trackModuleExists(contentModules, module)"
        @intersects="trackModuleViewed(contentModules, module)"
      >
        <SearchAndFilterModuleContainer
          :module="module"
        />
      </IntersectWrapper>

      <IntersectWrapper
        v-else-if="module.component === ContentModules.SLIDER"
        @mounted="trackModuleExists(contentModules, module)"
        @intersects="trackModuleViewed(contentModules, module)"
      >
        <CRSliderModule
          v-append-parameters="urlParams"
          :module="module"
          :lazy="index > 2"
          :labels="sliderLabels"
          @emitted-click="
            (e) => {
              trackModuleClick(contentModules, module, e.child);
            }
          "
          @slide-visible="
            (e) => {
              trackModuleViewed(contentModules, module, e.child);
            }
          "
        />
      </IntersectWrapper>

      <IntersectWrapper
        v-else-if="module.component === ContentModules.HERO"
        @mounted="trackModuleExists(contentModules, module)"
        @intersects="trackModuleViewed(contentModules, module)"
      >
        <CRHero
          v-append-parameters="heroUrlParams"
          :module="getModuleWithPitchYouButton(module)"
          :job-data="jobDataForApply"
          :immediate-start-label="t('job-detail.start-date-now')"
          @emitted-click="onHeaderCtaClick"
        />
      </IntersectWrapper>

      <IntersectWrapper
        v-else-if="module.component === ContentModules.JOBTEXTFIELD && jobTextFieldHasContent(module, jobData)"
        @mounted="trackModuleExists(contentModules, module)"
        @intersects="trackModuleViewed(contentModules, module)"
      >
        <CRJobTextField
          :module="module"
          :job-data="jobData"
        />
      </IntersectWrapper>

      <IntersectWrapper
        v-else-if="module.component === ContentModules.FORM && showApplicationForm"
        @mounted="trackModuleExists(contentModules, module)"
        @intersects="onFormIntersect(contentModules, module)"
      >
        <CRFormRendererContainer :module="module" :job="jobData" />
      </IntersectWrapper>

      <IntersectWrapper
        v-else-if="module.component === ContentModules.CONTACT_PERSON && (!module.isDynamic || hasNonEmptyContactData(jobData))"
        @mounted="trackModuleExists(contentModules, module)"
        @intersects="trackModuleViewed(contentModules, module)"
      >
        <CRContactPerson
          v-append-parameters="urlParams"
          :module="module"
          :job-data="jobData"
          :use-job-data="module.isDynamic"
          :lazy="index > 2"
          @emitted-click="trackModuleClick(contentModules, module, $event)"
        />
      </IntersectWrapper>

      <IntersectWrapper
        v-else-if="module.component === ContentModules.COMPANY_INFO && companyInfoHasContent(module, jobData)"
        @mounted="trackModuleExists(contentModules, module)"
        @intersects="trackModuleViewed(contentModules, module)"
      >
        <CRCompanyInfo
          :module="module"
          :job-data="jobData"
          :use-job-data="module.isDynamic"
          :lazy="index > 2"
        />
      </IntersectWrapper>
    </div>

    <transition v-if="heroModule && shouldShowActionBar" name="slide-up">
      <CRActionBar
        v-show="showActionBar"
        :module="getModuleWithPitchYouButton(heroModule)"
        :action-bar-config="props.actionBarConfig"
        :job-data="jobDataForApply"
        :navigation-global="jobShop?.navigation"
        class="cr-action-bar"
        @emitted-click="onHeaderCtaClick"
      />
    </transition>
  </div>
</template>

<script setup lang="ts">
import { ContentModules } from "@/utils/enums/ContentModules"
import { ApplicationType } from "@/utils/enums/ApplicationType"
import { useTypesenseJobCounts } from "@/composables/useTypesenseJobCounts"
import { useActionBarScroll } from "@/composables/useActionBarScroll"
import { buildPitchYouUrl, generatePitchYouClickId } from "@/utils/pitchYou"
import CRFormRendererContainer from "@/components/CRFormRendererContainer.vue"

const props = defineProps<{
  contentModules: ContentModule[]
  jobData?: FlatJob
  actionBarConfig?: any
}>()

const urlParams = useQueryHeaders()
const { t, locales } = useI18n()
// Keep this above the `await` further down — see the composable's docs.
provideCrossroadsAiLabel(t)
const sliderLabels = computed(() => ({ slide: t("module.slider.slide"), of: t("module.slider.of"), previous: t("module.slider.previous"), next: t("module.slider.next") }))
const hiringProcessLabels = computed(() => ({ of: t("module.hiringProcess.of"), previousStep: t("module.hiringProcess.previousStep"), nextStep: t("module.hiringProcess.nextStep") }))
const { data: jobShop } = useNuxtData("jobShopData")
// Resolve the jobshop region tag ("de-DE") to a registered i18n code ("de") so the
// job-count translation resolves deterministically (see resolveI18nLocale).
const jobShopLocale = computed(() => resolveI18nLocale(jobShop.value?.locale, locales.value.map(l => l.code), "en"))

const vanity = jobShop.value?.jobShopCompanyVanity
const offerId = props.jobData?.offer_uuid
const pyclid = useState(`pyclid-${offerId}`, () =>
  vanity && offerId ? generatePitchYouClickId(vanity, offerId) : null)
// Resolved once during SSR by plugins/tracking-bag.server.ts, which merges the
// visitor's session before running the ladder, so the value is final by render.
// Read above the `await` below, per the async-setup rule in AGENTS.md.
const resolvedSource = useResolvedSource()

/**
 * The bag handed to the subtree holding the PitchYou link, with the resolved
 * applicant source in place of the `utm_source` that arrived.
 *
 * v-append-parameters overwrites a parameter the link already carries, which apply
 * URLs depend on — a PitchYou link arrives with `quelle=0` as a placeholder and the
 * visitor's real value has to win. So the source is not protected from the
 * directive; the directive is given the right value to write instead.
 *
 * Identical to `urlParams` until a JobShop configures `source-parameters`, since
 * the source then resolves to the `utm_source` that was already in the bag.
 */
const heroUrlParams = computed<QueryHeader[]>(() => {
  const source = resolvedSource.value

  if (!source)
    return urlParams.value

  // Replaced in place rather than appended, so the parameter order on the outbound
  // link does not change.
  let replaced = false
  const params = urlParams.value.map((param) => {
    if (param.key.toLowerCase() !== "utm_source")
      return param

    replaced = true
    return { key: param.key, value: source }
  })

  return replaced ? params : [...params, { key: "utm_source", value: source }]
})

/**
 * The job data with the click id and the resolved applicant source written onto
 * its PitchYou apply URL. The PitchYou link is the only place the source is
 * overwritten (JET-2719); every other link gets the parameters that arrived.
 *
 * The CRActionBar carries no v-append-parameters, so this is the only thing that
 * puts the source on its apply link. In the CRHero subtree the directive writes it
 * too, from `heroUrlParams`.
 */
const jobDataForApply = computed<FlatJob | undefined>(() => {
  const pitchYouUrl = buildPitchYouUrl(props.jobData?.pitch_you_url, {
    pyclid: pyclid.value,
    source: resolvedSource.value,
  })

  if (!pitchYouUrl || !props.jobData)
    return props.jobData

  return { ...props.jobData, pitch_you_url: pitchYouUrl }
})

const { jobsCountCategories, jobsCountAll, jobCountsUnavailable } = await useTypesenseJobCounts()

const zeroJobsText = computed(() =>
  jobCountsUnavailable.value ? "" : t("module.jobs-text", 0, { locale: jobShopLocale.value }),
)

const heroModule = computed(() => props.contentModules.find(m => m.component === ContentModules.HERO))

const { showActionBar, shouldShowActionBar, getModuleWithPitchYouButton } = useActionBarScroll({
  contentModules: props.contentModules,
  actionBarConfig: props.actionBarConfig,
  jobData: props.jobData,
})

const { onHeaderCtaClick, onFormIntersect } = useHeaderActionTracking(
  computed(() => props.jobData),
)

const showApplicationForm = computed(() => {
  const applicationType = props.jobData?.application_type

  if (!applicationType)
    return false

  return applicationType !== ApplicationType.EXTERNAL && applicationType !== ApplicationType.NONE
})

function hasNonEmptyContact(contact: FlatJobContact | undefined | null): boolean {
  if (!contact)
    return false
  const values = [
    contact.first_name,
    contact.last_name,
    contact.email,
    contact.phone,
    contact.city,
    contact.street,
    contact.job_position,
  ]
  return values.some(v => typeof v === "string" && v.trim() !== "")
}

function hasNonEmptyContactData(job: FlatJob | undefined): boolean {
  // Prefer the new contact_persons array; a module has content if any person does.
  if (Array.isArray(job?.contact_persons) && job.contact_persons.some(hasNonEmptyContact))
    return true
  // Legacy fallback: a single contact_data object.
  return hasNonEmptyContact(job?.contact_data)
}
</script>

<style lang="scss" scoped>
/* Fix subpixel gaps between modules caused by container query layout containment (see JET-90) */
.content-module-wrapper {
  margin-bottom: -1px;
}

.cr-action-bar {
  position: fixed;
  z-index: 90;
  bottom: 0;
  left: 0;

  width: 100%;
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0;
}
</style>
